/**
 * useManualStopContact - reveal a customer's contact details on demand.
 *
 * Disabled until `enabled` is true (the dispatcher clicked "Reveal"), because
 * every read is written to the PII access log. Cached briefly so collapsing and
 * re-expanding a card doesn't create a second log entry.
 */

import { useQuery } from '@tanstack/react-query';
import { getManualStopContacts } from '@/lib/supabase/manualStopContacts';
import type { ContactAccessContext, ManualStopContact } from '@/types/manualStops';

export const manualStopContactKeys = {
  all: ['manual-stop-contacts'] as const,
  detail: (contactId: string) => [...manualStopContactKeys.all, contactId] as const
};

export function useManualStopContact(
  contactId: string | null | undefined,
  context: ContactAccessContext,
  enabled: boolean
) {
  return useQuery<ManualStopContact | null>({
    queryKey: contactId ? manualStopContactKeys.detail(contactId) : manualStopContactKeys.all,
    queryFn: async () => {
      const rows = await getManualStopContacts([contactId as string], context);
      return rows[0] ?? null;
    },
    enabled: enabled && !!contactId,
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false
  });
}

export { formatUsPhone } from '@/lib/manualStops/formatUsPhone';
